import {useEffect} from 'react';

import {ClickAwayListener, Dialog, Paper, Popper} from '@mui/material';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import {useForm} from 'react-hook-form';

import {BREAKPOINTS, REPORT_PARAMETERS_ACCORDIONS_IDS} from '../../../../../../const';
import useBreakpoint from '../../../../../../hooks/dom/useBreakpoint';
import useReportParameters from '../../../../../../hooks/providers/useReportParameters';
import BaseModal from '../../../../../modal/BaseModal';
import PartialUpdateFooter from '../PartialUpdateFooter/PartialUpdateFooter';
import PartialUpdateHeader from '../PartialUpdateHeader/PartialUpdateHeader';
import PartialUpdateTableList from '../PartialUpdateTablesList/PartialUpdateTablesList';

const PartialUpdateSelectTablesPopper = ({isOpen, onClickAway, anchorEl}) => {
  const {reportTables, runDatasetRefresh, setReportParametersCategoryExpanded} = useReportParameters();

  const methods = useForm({defaultValues: {}});
  const currentBreakpoint = useBreakpoint();
  const isExtraSmallScreen = currentBreakpoint === BREAKPOINTS.xs;

  useEffect(() => {
    if (reportTables?.length) {
      methods.reset(reportTables.reduce((acc, table) => ({...acc, [table]: false}), {}));
    }
  }, [reportTables]);

  const values = methods.watch();
  const selectedTables = Object.entries(values)
    .filter(([, checked]) => checked)
    .map(([table]) => table);

  const onSubmit = v => {
    const tables = Object.keys(v).filter(key => v[key]);
    runDatasetRefresh(tables);
    setReportParametersCategoryExpanded(REPORT_PARAMETERS_ACCORDIONS_IDS.UPDATES);
    onClickAway();
  };

  const selectAll = e => {
    e.stopPropagation();
    reportTables.forEach(t => methods.setValue(t, true));
  };

  const unselectAll = e => {
    e.stopPropagation();
    reportTables.forEach(t => methods.setValue(t, false));
  };

  const renderDesktopForm = () => {
    return (
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Paper elevation={2}>
          <Box sx={{overflow: 'auto', maxWidth: 500, maxHeight: 300}}>
            <PartialUpdateHeader onSelectAll={selectAll} onUnselectAll={unselectAll} />

            <PartialUpdateTableList tables={reportTables} control={methods.control} />
          </Box>

          <PartialUpdateFooter selectedCount={selectedTables.length} totalCount={reportTables.length} />
        </Paper>
      </form>
    );
  };

  const renderMobileForm = () => {
    return (
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <PartialUpdateHeader onSelectAll={selectAll} onUnselectAll={unselectAll} />

        <PartialUpdateTableList tables={reportTables} control={methods.control} />

        <PartialUpdateFooter selectedCount={selectedTables.length} totalCount={reportTables.length} />
      </form>
    );
  };

  if (!isExtraSmallScreen) {
    return (
      <Popper
        placement="left"
        open={isOpen}
        anchorEl={anchorEl}
        disablePortal={false}
        sx={{
          right: '10px !important',
          zIndex: 9999,
          width: 400
        }}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        <ClickAwayListener onClickAway={onClickAway}>{renderDesktopForm()}</ClickAwayListener>
      </Popper>
    );
  }

  return (
    <BaseModal onClose={onClickAway} title="Sélectionner les tables à actualiser" isFullScreen open={isOpen} hasCloseIcon>
      {renderMobileForm()}
    </BaseModal>
  );
};

PartialUpdateSelectTablesPopper.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClickAway: PropTypes.func.isRequired,
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Element)])
};

export default PartialUpdateSelectTablesPopper;
